import React, { useEffect, useState } from 'react';
import MiniGardenImage from "./MiniGardenImage";
import CustomDropdown from "./reusableComponents/CustomDropdown";
import { Trash, Copy, RotateCw, Save, CheckCircle } from 'lucide-react';

const SOILTYPES = [
    { value: 'loamy', label: 'Loamy' },
    { value: 'sandy', label: 'Sandy' },
    { value: 'clay', label: 'Clay' },
    { value: 'silty', label: 'Silty' },
    { value: 'peaty', label: 'Peaty' },
    { value: 'chalky', label: 'Chalky' },
    { value: 'saline', label: 'Saline' }
]

const HARDINESSZONES = [
    { value: '1a', label: '1a' },
    { value: '1b', label: '1b' },
    { value: '2a', label: '2a' },
    { value: '2b', label: '2b' },
    { value: '3a', label: '3a' },
    { value: '3b', label: '3b' },
    { value: '4a', label: '4a' },
    { value: '4b', label: '4b' },
    { value: '5a', label: '5a' },
    { value: '5b', label: '5b' },
    { value: '6a', label: '6a' },
    { value: '6b', label: '6b' },
    { value: '7a', label: '7a' },
    { value: '7b', label: '7b' },
    { value: '8a', label: '8a' },
    { value: '8b', label: '8b' },
    { value: '9a', label: '9a' },
    { value: '9b', label: '9b' },
    { value: '10a', label: '10a' },
    { value: '10b', label: '10b' },
    { value: '11a', label: '11a' },
    { value: '11b', label: '11b' },
    { value: '12a', label: '12a' },
    { value: '12b', label: '12b' },
    { value: '13a', label: '13a' },
    { value: '13b', label: '13b' }
]

const SUNLIGHTLEVELS = [
    { value: 'Full Sun', label: 'Full Sun' },
    { value: 'Partial Sun', label: 'Partial Sun' },
    { value: 'Partial Shade', label: 'Partial Shade' },
    { value: 'Full Shade', label: 'Full Shade' }
]

const MAINTENANCELEVELS = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
]
const soilTypes = ["Loamy", "Sandy", "Clay", "Silty", "Peaty", "Chalky", "Saline"];
const hardinessZones = ["1a", "1b", "2a", "2b", "3a", "3b", "4a", "4b", "5a", "5b", "6a", "6b", "7a", "7b", "8a", "8b", "9a", "9b", "10a", "10b", "11a", "11b", "12a", "12b", "13a", "13b"];
const sunlightLevels = ["Full Sun", "Partial Sun", "Partial Shade", "Full Shade"];
const maintenanceLevels = ["Low", "Medium", "High"];

const DenotedGardenMenuItem = ({ gardenName, denotedPlots, gardenCells, width, height, onDelete, onSave, onRotate, onDuplicate, isMainscreen=false }) => {
    const [normalizedMatrix, setNormalizedMatrix] = useState([]);
    const [normalizedWidth, setNormalizedWidth] = useState(0);
    const [normalizedHeight, setNormalizedHeight] = useState(0);
    const [totalSpace, setTotalSpace] = useState(0);
    const [selectedSoil, setSelectedSoil] = useState("Loamy"); // Default soil type
    const [selectedHardiness, setSelectedHardiness] = useState("5b"); // Default hardiness zone
    const [selectedSunlight, setSelectedSunlight] = useState("Full Sun"); // Default sunlight level
    const [selectedMaintenance, setSelectedMaintenance] = useState("Low"); // Default maintenance level
    const [isSaved, setIsSaved] = useState(false); // for changing background to show it is saved

    function normalizeArray(dataString) {
        let array = dataString.trim().split("\n").map(row => row.split(",").map(Number));
        array = array.filter(row => row.some(value => value !== 0));
        let transposed = array[0].map((_, colIndex) => array.map(row => row[colIndex]));
        transposed = transposed.filter(col => col.some(value => value !== 0));
        return transposed[0].map((_, rowIndex) => transposed.map(col => col[rowIndex]));
    }

    function getTotalSpace() {
        const total = gardenCells.split('\n').map(row => row.split(',').map(Number)).flat().reduce((acc, curr) => acc + curr, 0);
        setTotalSpace(total);
    }

    function handleSaving() {
        setIsSaved(true);
        onSave(gardenName, gardenCells, selectedSoil, selectedHardiness, selectedSunlight, selectedMaintenance);
        // setTimeout(() => setIsSaved(false), 3000);
    }

    function handleRotateClick () {
        onRotate(gardenName);
    }

    function handleDuplicateClick() {
        // With the new structure, garden name is the key, and plots array is the value
        // So we directly access the plots using the garden name
        const plotArray = denotedPlots[gardenName];

        if (plotArray) {
            // Make sure to pass the garden name and its associated plot array
            onDuplicate(gardenName, plotArray);
        } else {
            console.error(`No plot found for garden named: ${gardenName}`);
        }
    }

    const handleSoilChange = React.useCallback((soil) => {
        setSelectedSoil(soil.value);
    }, []);

    const handleHardinessChange = React.useCallback((hardiness) => {
        setSelectedHardiness(hardiness.value);
    }, []);

    const handleSunlightChange = React.useCallback((sunlight) => {
        setSelectedSunlight(sunlight.value);
    }, []);

    const handleMaintenanceChange = React.useCallback((maintenance) => {
        setSelectedMaintenance(maintenance.value);
    }, []);

    useEffect(() => {
        const normalized = normalizeArray(gardenCells);
        setNormalizedMatrix(normalized);
        setNormalizedHeight(normalized.length);
        setNormalizedWidth(normalized[0]?.length || 0);
        getTotalSpace();
    }, [gardenCells]);

    return (
        <div className={`denoted-garden-menu-item ${isSaved ? "saved-bg" : ""} ${isMainscreen ? "mainscreen" : ""}`}>
            <MiniGardenImage gardenMatrix={gardenCells}/>

            <div className={"denoted-garden-menu-item-info"}>
                <h2 className="denoted-garden-menu-item-garden-name">{gardenName}</h2>
                <div className={"denoted-garden-menu-size-display-div"}>
                    <h4 className={"denoted-garden-menu-item-space"}>{totalSpace} ft&sup2;</h4>
                    <div className="vertical-divider"></div>
                    <h4 className={"denoted-garden-menu-item-plot-size"}>{normalizedWidth}' x {normalizedHeight}'</h4>
                </div>
                <h4 className={"how-many-plants-text"} title={"How many plants you can fit in your garden!"}>&asymp; {Math.round(totalSpace / 4)} plants</h4>
            </div>

            <div className={"denoted-garden-menu-item-soil-type"}>
                <h4 className={"soil-type-title"}>Soil Type: </h4>
                <CustomDropdown
                    options={SOILTYPES}
                    value={selectedSoil}
                    onChange={handleSoilChange}
                    placeholder="Select a soil type..."
                    className="garden-dropdown-custom"
                />
            </div>

            <div className={"denoted-garden-menu-item-soil-type"}>
                <h4 className={"soil-type-title"}>Hardiness Zone: </h4>
                <CustomDropdown
                    options={HARDINESSZONES}
                    value={selectedHardiness}
                    onChange={handleHardinessChange}
                    placeholder="Select a hardiness zone..."
                    className="garden-dropdown-custom"
                />
            </div>

            <div className={"denoted-garden-menu-item-soil-type"}>
                <h4 className={"soil-type-title"}>Sunlight Levels: </h4>
                <CustomDropdown
                    options={SUNLIGHTLEVELS}
                    value={selectedSunlight}
                    onChange={handleSunlightChange}
                    placeholder="Select a sunlight level..."
                    className="garden-dropdown-custom"
                />
            </div>

            <div className={"denoted-garden-menu-item-soil-type"}>
                <h4 className={"soil-type-title"}>Maintenance: </h4>
                <CustomDropdown
                    options={MAINTENANCELEVELS}
                    value={selectedMaintenance}
                    onChange={handleMaintenanceChange}
                    placeholder="Select a maintenance level..."
                    className="garden-dropdown-custom"
                />
            </div>

            <div className="denoted-garden-menu-item-button-quad">
                <div className="denoted-garden-menu-item-trash">
                    <button className={`basic-button-clickable-icon`} title={"Delete Garden"}
                            onClick={() => onDelete(gardenName)}
                    >
                        <Trash size={24} strokeWidth={2} color="#f3f3f3" className={"garden-style-dropdown-icon"}/>
                    </button>
                </div>
                <div className="denoted-garden-menu-item-trash">
                    <button className={`basic-button-clickable-icon`} title={"Duplicate Garden"}
                            onClick={() => handleDuplicateClick()}
                    >
                        <Copy size={24} strokeWidth={2} color="#f3f3f3" className={"garden-style-dropdown-icon"}/>
                    </button>
                </div>
                <div className="denoted-garden-menu-item-trash">
                    <button className={`basic-button-clickable-icon`} title={"Rotate Garden"}
                            onClick={() => handleRotateClick()}
                    >
                        <RotateCw size={24} strokeWidth={2} color="#f3f3f3" className={"garden-style-dropdown-icon"}/>
                    </button>
                </div>
                <div className="denoted-garden-menu-item-trash">
                    <button
                        className={`basic-button-clickable-icon`}
                        title={isSaved ? "Garden Saved" : "Save Garden"}
                        onClick={() => handleSaving()}
                    >
                        {isSaved ? (
                            <CheckCircle size={24} strokeWidth={2} color="#f3f3f3"
                                           className={"garden-style-dropdown-icon"}/>
                        ) : (
                            <Save size={24} strokeWidth={2} color="#f3f3f3" className={"garden-style-dropdown-icon"}/>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DenotedGardenMenuItem;
